import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Users, CreditCard, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const icons = {
  speed: Zap,
  quality: Shield,
  support: Users,
  price: CreditCard
};

const colors = {
  speed: 'from-yellow-500 to-orange-500',
  quality: 'from-blue-500 to-cyan-500',
  support: 'from-purple-500 to-pink-500',
  price: 'from-green-500 to-emerald-500'
};

export function ValueProposition() {
  const { t } = useLanguage();
  const items = ['speed', 'quality', 'support', 'price'];

  return (
    <section className="py-24 md:py-32 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
      
      {/* Floating elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-mono text-sm tracking-wider uppercase mb-4 block">
            Why choose us
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('value.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('value.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {items.map((item, index) => {
            const Icon = icons[item];
            return (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div 
                  className="relative bg-slate-900/80 border border-slate-800 backdrop-blur-xl p-8 rounded-2xl overflow-hidden hover:border-primary/50 transition-all duration-500 hover:-translate-y-2 card-3d"
                  data-testid={`value-card-${item}`}
                >
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${colors[item]} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors[item]} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-heading text-2xl font-bold mb-3 flex items-center gap-2">
                    {t(`value.${item}.title`)}
                    <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    {t(`value.${item}.description`)}
                  </p>

                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20">
                    <div className={`absolute top-4 right-4 w-2 h-2 rounded-full bg-gradient-to-br ${colors[item]} opacity-50`} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
